import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SpeedIcon from '@mui/icons-material/Speed';
import DevicesIcon from '@mui/icons-material/Devices';

import logo from 'utils/images/tattersall-white.png';

const NEW_SITE_URL = 'https://v2.tattersallgda.cl';
const STORAGE_KEY = 'newSitePromo:lastSeenAt';
// Show again after this many days
const COOLDOWN_DAYS = 7;
// Bump this to force showing the dialog again to all users
const CAMPAIGN_VERSION = '2026-04-v1';
const CAMPAIGN_KEY = 'newSitePromo:campaignVersion';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const FeatureItem = ({ icon, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: 'rgba(255,255,255,0.18)',
        color: '#fff',
        flexShrink: 0,
      }}>
      {icon}
    </Box>
    <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontSize: { xs: 14, sm: 15 } }}>
      {text}
    </Typography>
  </Stack>
);

function NewSitePromoDialog() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const savedCampaign = localStorage.getItem(CAMPAIGN_KEY);
      const lastSeen = localStorage.getItem(STORAGE_KEY);

      // If campaign changed, force show.
      if (savedCampaign !== CAMPAIGN_VERSION) {
        setTimeout(() => setOpen(true), 1200);
        return;
      }

      // Otherwise respect cooldown.
      if (lastSeen) {
        const diffDays = (Date.now() - Number(lastSeen)) / (1000 * 60 * 60 * 24);
        if (diffDays < COOLDOWN_DAYS) return;
      }
      setTimeout(() => setOpen(true), 1200);
    } catch (_) {
      setTimeout(() => setOpen(true), 1200);
    }
  }, []);

  const persistDismissal = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      localStorage.setItem(CAMPAIGN_KEY, CAMPAIGN_VERSION);
    } catch (_) {
      // ignore
    }
  };

  const handleClose = () => {
    persistDismissal();
    setOpen(false);
  };

  const handleGoToNewSite = () => {
    persistDismissal();
    window.open(NEW_SITE_URL, '_blank', 'noopener');
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 45%, #3949ab 100%)',
          color: '#fff',
          position: 'relative',
        },
      }}>
      {/* Decorative blurred blobs */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,193,7,0.35) 0%, rgba(255,193,7,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -120,
          left: -100,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(41,182,246,0.28) 0%, rgba(41,182,246,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <IconButton
        aria-label="Cerrar"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'rgba(255,255,255,0.8)',
          zIndex: 2,
          '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
        }}>
        <CloseIcon />
      </IconButton>

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, sm: 4.5 } }}>
        <Stack spacing={2.5} alignItems="flex-start">
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'rgba(255,193,7,0.18)',
              border: '1px solid rgba(255,193,7,0.5)',
            }}>
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#ffc107' }} />
            <Typography
              sx={{
                color: '#ffecb3',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}>
              Nuevo sitio disponible
            </Typography>
          </Box>

          <Box>
            <img src={logo} alt="Tattersall GDA" height={32} style={{ marginBottom: 12 }} />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 24, sm: 30 },
                lineHeight: 1.2,
                mb: 1,
              }}>
              Estrenamos nueva plataforma
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: { xs: 14, sm: 16 },
                lineHeight: 1.55,
              }}>
              Rediseñamos Tattersall GDA para que encuentres los remates y lotes más rápido, con una
              experiencia moderna y optimizada. ¡Pruébala y cuéntanos qué te parece!
            </Typography>
          </Box>

          <Stack
            spacing={1.25}
            sx={{
              width: '100%',
              pt: 0.5,
            }}>
            <FeatureItem icon={<SpeedIcon fontSize="small" />} text="Más rápida y ligera" />
            <FeatureItem
              icon={<DevicesIcon fontSize="small" />}
              text="Diseño responsive para móvil, tablet y escritorio"
            />
            <FeatureItem
              icon={<AutoAwesomeIcon fontSize="small" />}
              text="Búsqueda y navegación renovadas"
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ width: '100%', pt: 1.5 }}>
            <Button
              onClick={handleGoToNewSite}
              variant="contained"
              size="large"
              startIcon={<RocketLaunchIcon />}
              sx={{
                bgcolor: '#ffc107',
                color: '#1a237e',
                fontWeight: 700,
                px: 3,
                py: 1.25,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: 15,
                boxShadow: '0 8px 20px rgba(255,193,7,0.35)',
                '&:hover': {
                  bgcolor: '#ffb300',
                  boxShadow: '0 10px 24px rgba(255,193,7,0.45)',
                },
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
              }}>
              Probar el nuevo sitio
            </Button>
            <Button
              onClick={handleClose}
              size="large"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 14,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: '#fff',
                },
              }}>
              Seguir aquí por ahora
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}

export default NewSitePromoDialog;
